import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { TableCell, TableRow } from '@mui/material';
import Content from 'components/content/content';
import CONSTANTS from 'modules/constant';
import GLOBAL from 'modules/global';
import type { ErrorProps, ResaleValueGuarantee } from 'modules/types';
import Notification from 'components/notification';
import {
  useGetRvgListMutation,
  useExportRvgMutation
} from 'store/resaleValueGuarantee';
import { type PartialResaleValueGuarantee } from "modules/partial";

const ResaleValueGuaranteeList = () => {
  const [getListSubmissions, submissionsList] = useGetRvgListMutation();
  const [exportSubmissions, exportData] = useExportRvgMutation();

  const [payload, setPayload] = useState<PartialResaleValueGuarantee>({
    start: 0,
    length: CONSTANTS.DEFAULT_PAGINATION_PER_PAGE,
    search: '',
  });

  const formatDate = (e: string) => (!isEmpty(e) ? moment(e).format('DD-MM-YYYY HH:mm') : '-');
  const onSearch = (value: string) => setPayload({ ...payload, start: 0, search: value });
  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 });
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value });

  const getField = () => columns.filter(c => c.searchable !== false).map(c => c.key).join(',')

  const onResync = () => {
    const formData = new FormData();

    formData.append('start', payload.start.toString());
    formData.append('length', payload.length.toString());
    formData.append('field', getField())

    if (!isEmpty(payload.search)) formData.append('search[value]', payload.search);

    getListSubmissions(formData);
  };

  const onDownload = (type?: string) => {
    const formData = new FormData();

    // For export, typically fetch all data, so page 1 and max limit
    formData.append('start', '0');
    formData.append('length', CONSTANTS.DEFAULT_PAGINATION_MAXIMUM_DOWNLOAD.toString());
    formData.append('field', getField())

    if (!isEmpty(payload.search)) formData.append('search[value]', payload.search);
    if (type) formData.append('Download-Type', type); // Assuming backend uses this for format

    exportSubmissions(formData);
  };

  const columns = [
    {
      column: 'Member ID',
      label: 'Member ID',
      searchable: true,
      key: 'memberCode',
    },
    {
      column: 'Full Name',
      label: 'Full Name',
      searchable: true,
      key: 'fullName',
    },
    {
      column: 'Phone Number',
      label: 'Phone Number',
      searchable: true,
      key: 'phoneNumber',
    },
    {
      column: 'VIN',
      label: 'VIN',
      searchable: true,
      key: 'vin',
    },
    {
      column: 'Model',
      label: 'Model',
      searchable: true,
      key: 'model',
    },
    {
      column: 'Handover Date',
      label: 'Handover Date',
      key: 'handoverDate',
      searchable: false,
      render: (row: ResaleValueGuarantee) => formatDate(row.handoverDate),
    },
    {
      column: 'Notification Type',
      label: 'Notification Type',
      key: 'notificationType',
    },
    {
      column: 'Notification Sent Date',
      label: 'Notification Sent Date',
      key: 'notificationSentDate',
      searchable: false,
      render: (row: ResaleValueGuarantee) => formatDate(row.notificationSentDate),
    },
    {
      column: 'Form Submission Date',
      label: 'Form Submission Date',
      key: 'formSubmissionDate',
      searchable: false,
      render: (row: ResaleValueGuarantee) => formatDate(row.formSubmissionDate),
    },
    {
      column: 'Domicile',
      label: 'Domicile',
      searchable: true,
      key: 'domicile',
    },
    {
      column: 'Province',
      label: 'Province',
      searchable: true,
      key: 'province',
    },
    {
      column: 'City',
      label: 'City',
      searchable: true,
      key: 'city',
    },
  ];

  const tableHeadTitles = columns.map(c => c.label);

  const totalPage =
    submissionsList.data && GLOBAL.tableTotalPage(submissionsList.data.recordsTotal);
  const numbers = GLOBAL.tableNumber(payload.start);

  useEffect(() => {
    onResync();
  }, [payload]);

  return (
    <>
      <Content
        title='Resale Value Guarantee Submissions'
        tableHeadTitles={tableHeadTitles}
        onSearch={onSearch}
        onResync={onResync}
        onDownload={onDownload}
        onDownloadTypeIgnored={['pdf']}
        downloadLoading={exportData.isLoading}
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        totalRecords={submissionsList.data && submissionsList.data.recordsTotal}
        page={payload.start + 1}
        isLoading={submissionsList.isLoading}
        useDownloadDropdown
        useAction={false}
      >
        {submissionsList.isSuccess &&
          submissionsList.data &&
          submissionsList.data.data?.map((table, index) => (
            <TableRow key={index}>
              <TableCell>{numbers[index] + 1}</TableCell>
              {columns.map(col =>
                <TableCell key={col.key}>
                  {col.render ? col.render(table as ResaleValueGuarantee) : (table)[col.key as keyof ResaleValueGuarantee] || '-'}
                </TableCell>)}
            </TableRow>
          ))}
      </Content>


      <Notification
        open={!exportData.isLoading && (exportData.isSuccess || exportData.isError)}
        onClose={() => {
          exportData.reset()
        }}
        isError={exportData.isError}
        message={GLOBAL.returnExceptionMessage(
          exportData.isError,
          exportData.error as ErrorProps
        )}
      />
    </>
  );
};

export default ResaleValueGuaranteeList;